/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useCallback, useEffect } from "react";
import { Box, Dialog, Grid, Typography } from "@mui/material";
import Button from "components/generals/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HeaderPersonInformation from "components/views/HeaderPersonInformation";
import AccountingMovementsTotals from "components/views/AccountingMovementsTotals";
import AccountingMovementsTable from "components/tables/AccountingMovementsTable";
import PaymentForm from "components/forms/PaymentForm";
import PermissionWrapper from "components/generals/PermissionWrapper";
import {
  PatientInterface,
  AccountingMovementInterface,
  QueryResultInterface,
  HeaderEnum,
  PersonInterface,
} from "@malmur-technologies/medicalsystem-shared";
import { FormActionType, EntityPermissions, PermissionsUser } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import { Print as PrintIcon } from "@mui/icons-material";
import { PrintAccountingMovements } from "../templates/PrintAccountingMovementsTemplate";
import PrintFiltersModal from "components/views/PrintFiltersModal";
import { SearchParamsInterface } from "@malmur-technologies/medicalsystem-shared";
import { DateFilterTypeEnum } from "@malmur-technologies/medicalsystem-shared";

/**
 * Props for the AccountsMovementsDataContent component.
 * @interface AccountingsMovementsDataContentProps
 * @property {string} accountingId - The accounting record ID to display movements for
 * @property {PatientInterface} patient - The patient associated with the accounting record
 */
interface AccountingsMovementsDataContentProps {
  accountingId: string;
  patient: PatientInterface;
}

/**
 * Content component for displaying and managing accounting movements.
 * Shows detailed financial movements (charges, payments, adjustments) for a patient's
 * accounting record. Includes functionality for creating payments, viewing totals,
 * printing statements, and filtering movements by date range.
 * @param {AccountingsMovementsDataContentProps} props - Component props
 * @returns {React.ReactElement} The accounting movements content component
 */
const AccountsMovementsDataContent: React.FC<AccountingsMovementsDataContentProps> = ({
  accountingId,
  patient,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [movements, setMovements] = useState<AccountingMovementInterface[]>([]);
  const [openPaymentForm, setOpenPaymentForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [_isLoading, _error, sendRequest] = useHttp();
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const handleClosePaymentForm = () => {
    setOpenPaymentForm(false);
  };


  const fetchMovements = useCallback(() => {
    if (accountingId) {
      const endpoint = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`;

      sendRequest<QueryResultInterface<AccountingMovementInterface>, void>(
        endpoint,
        (result) => {
          const movementsArray = Array.isArray(result) ? result : result.results || [];
          setMovements(movementsArray);
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }]
      );
    }
  }, [accountingId, sendRequest]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handlePaymentSuccess = useCallback(() => {
    fetchMovements();
    setRefreshKey((prev) => prev + 1);
    setOpenPaymentForm(false);
  }, [fetchMovements]);

  const handlePrintClick = () => {
    setIsFiltersModalOpen(true);
  };

  const handleCloseFiltersModal = () => {
    setIsFiltersModalOpen(false);
  };

  const handleApplyFilters = (
    filters: SearchParamsInterface,
    filteredMovements: AccountingMovementInterface[]
  ) => {
    const isFiltered = !!(filters.date || filters.selectedMonth || filters.selectedYear || filters.startDate || filters.endDate);

    const filterInfo = isFiltered
      ? {
        type: getFilterTypeFromBackendFilters(filters),
        date: filters.date,
        selectedMonth: filters.selectedMonth,
        selectedYear: filters.selectedYear,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }
      : undefined;

    const printContent = PrintAccountingMovements({
      movements: filteredMovements,
      facilityName: "", // TODO: get facility name
      facilityAddress: "", // TODO: get facility address
      personInfo: patient.person as PersonInterface,
      t,
      isFiltered: isFiltered,
      filterInfo: filterInfo,
    });

    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const printWindowWidth = 900;
    const printWindowHeight = 700;

    const left = screenWidth / 2 - printWindowWidth / 2;
    const top = screenHeight / 2 - printWindowHeight / 2;

    const printWindow = window.open(
      "",
      "",
      `width=${printWindowWidth},height=${printWindowHeight},left=${left},top=${top}`
    );

    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
    setIsFiltersModalOpen(false);
  };

  const getFilterTypeFromBackendFilters = (filters: SearchParamsInterface): string => {
    if (filters.date) return DateFilterTypeEnum.SPECIFIC_DATE;
    if (filters.selectedMonth !== undefined && filters.selectedYear !== undefined)
      return DateFilterTypeEnum.SPECIFIC_MONTH;
    if (filters.selectedYear !== undefined) return DateFilterTypeEnum.SPECIFIC_YEAR;
    if (filters.startDate && filters.endDate) return DateFilterTypeEnum.DATE_RANGE;
    return "all";
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        {t("accountingMovements.details")}
      </Typography>
      <Grid container spacing={2} className="accounting-movements-grid">
        <Grid item xs={12} md={6} className="grid-item-flex">
          <Box className="grid-item-wrapper">
            <HeaderPersonInformation personInformation={patient.person} progressNote={false} />
          </Box>
        </Grid>
        <Grid item xs={12} md={4} className="grid-item-flex">
          <Box className="grid-item-wrapper">
            <AccountingMovementsTotals movements={movements} />
          </Box>
        </Grid>

        <Grid item xs={12} md={2} className="grid-item-flex">
          <Box className="buttons-wrapper">
            <Button
              fullWidth
              variant="contained"
              color="info"
              onClick={() => navigate(`/payment?accountingId=${accountingId}`)}
            >
              {t("accountingMovements.payments")}
            </Button>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PAYMENTS].CREATE]}
            >
              <Button
                variant="contained"
                color="primary"
                id="new-payment-button"
                fullWidth
                onClick={() => setOpenPaymentForm(true)}
              >
                {t("accountingMovements.newPay")}
              </Button>
            </PermissionWrapper>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].PRINT]}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrintClick}
                disabled={movements.length === 0}
                fullWidth
              >
                {t("accountingMovements.print")}
              </Button>
            </PermissionWrapper>
          </Box>
        </Grid>
      </Grid>
      <AccountingMovementsTable
        accountingId={accountingId}
        key={refreshKey}
        patient={patient}
      />

      <Dialog
        open={openPaymentForm}
        onClose={handleClosePaymentForm}
        maxWidth={false}
        className="payment-form-dialog"
      >
        <PaymentForm
          accountingId={accountingId}
          onCloseAction={handleClosePaymentForm}
          action={FormActionType.NEW}
          onSuccess={handlePaymentSuccess}
        />
      </Dialog>
      <PrintFiltersModal
        open={isFiltersModalOpen}
        onClose={handleCloseFiltersModal}
        onApplyFilters={handleApplyFilters}
        movements={movements}
        accountingId={accountingId}
        t={t}
      />
    </Box>
  );
};

export default AccountsMovementsDataContent;